import { ArrowLeftRight } from 'lucide-react';
import { useState } from 'react';
import * as React from 'react';
import {
  MdDashboard,
  MdLocalShipping,
  MdPeople,
  MdFolder,
  MdDescription,
  MdSettings,
  MdMenu,
  MdClose
} from 'react-icons/md';

interface MenuItem {
  id: string;
  icon: React.ReactNode;
  label: string;
}

interface MobileHamburgerNavProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const MobileHamburgerNav: React.FC<MobileHamburgerNavProps> = ({ activeTab, setActiveTab }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuItems: MenuItem[] = [
    { id: 'dashboard', icon: <MdDashboard size={22} />, label: 'Home' },
    { id: 'transports', icon: <MdLocalShipping size={22} />, label: 'Transport' },
    { id: 'clients', icon: <MdPeople size={22} />, label: 'Clienți' },
    { id: 'management', icon: <MdFolder size={22} />, label: 'Manage' },
    { id: 'transferuri', icon: <ArrowLeftRight size={22} />, label: 'Transfer' },
    { id: 'cmr', icon: <MdDescription size={22} />, label: 'CMR' },
    { id: 'settings', icon: <MdSettings size={22} />, label: 'Setări' },
  ];

  const handleMenuItemClick = (id: string) => {
    setActiveTab(id);
    setIsMenuOpen(false);
  };

  return (
    <>
      {/* Hamburger Button - Fixed Top Center */}
      <div className="md:hidden fixed top-0 left-1/2 transform -translate-x-1/2 -translate-y-3 z-50">
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="relative p-3 bg-gradient-to-br from-orange-500 to-red-500 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110 active:scale-95"
          aria-label="Toggle menu"
        >
          <div className="relative z-10">
            {isMenuOpen ? (
              <MdClose size={22} className="text-white" />
            ) : (
              <MdMenu size={22} className="text-white" />
            )}
          </div>
          {/* Glow effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-red-400 rounded-full blur-md opacity-50 animate-pulse"></div>
        </button>
      </div>

      {/* Overlay with blur */}
      {isMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/30 backdrop-blur-sm z-40 transition-opacity duration-300"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Slide Menu */}
      <div
        className={`md:hidden fixed left-1/2 transform -translate-x-1/2 top-0 w-80 max-w-[90vw] bg-white shadow-2xl rounded-b-3xl transition-all duration-500 ease-out z-40 ${
          isMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}
      >
        {/* Header with gradient */}
        <div className="relative pt-14 pb-6 px-6 bg-gradient-to-br from-orange-500 via-orange-600 to-red-500 rounded-b-3xl overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
          
          <div className="relative z-1">
            <h2 className="text-xl font-bold text-white text-center mb-1">Navigare</h2>
            <p className="text-orange-100 text-xs text-center">VGL Trans Express</p>
          </div>
        </div>

        {/* Menu Items */}
        <nav className="p-5 pb-8">
          <div className="space-y-2">
            {menuItems.map(({ id, icon, label }) => (
              <button
                key={id}
                onClick={() => handleMenuItemClick(id)}
                className={`group w-full flex items-center p-4 rounded-2xl transition-all duration-300 ${
                  activeTab === id
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/30 scale-[1.02]'
                    : 'text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                }`}
              >
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 ${
                    activeTab === id
                      ? 'bg-white/20'
                      : 'bg-gray-100 group-hover:bg-orange-100 group-hover:scale-110'
                  }`}
                >
                  <div className={activeTab === id ? 'text-white' : 'text-orange-500'}>
                    {icon}
                  </div>
                </div>
                <span className="ml-4 text-base font-semibold tracking-wide">
                  {label}
                </span>
                {activeTab === id && (
                  <div className="ml-auto flex items-center space-x-1">
                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse delay-75"></div>
                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse delay-150"></div>
                  </div>
                )}
              </button>
            ))}
          </div>
        </nav>

        {/* Bottom decoration */}
        <div className="px-5 pb-5">
          <div className="h-1 bg-gradient-to-r from-transparent via-orange-300 to-transparent rounded-full"></div>
        </div>
      </div>
    </>
  );
};

export default MobileHamburgerNav;